import { attr } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import type Hospitalisation from "parog-web/models/hospitalisation";
import JournalEvent from "parog-web/models/journal-event";
import type Performer from "parog-web/models/performer";
import type Prescriptor from "parog-web/models/prescriptor";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

type RiskStringEnum =
  | "MALADIE"
  | "MATERNITE"
  | "ACCIDENT"
  | "TRAVAIL"
  | "PREVENTION";

type ExemptionStringEnum = "ALD";

/**
 * Describe Vaccines reimbursement.
 * Property descriptions are sometimes copy/pasted from the DMP specification.
 *
 * @see sel-mp-005_hr-integre-lps_v-1.9.pdf for details.
 */
export default class VaccinesRefundedJournalEvent extends JournalEvent {
  static type = "vaccines-refunded-journal-event";

  @attr("hospitalisation") declare hospitalisation: Hospitalisation;

  /**
   * Nature du risque au titre duquel la prestation est prise en charge.
   * Valeurs possibles : «MALADIE», «MATERNITE», «ACCIDENT», «TRAVAIL»,
   * «PREVENTION»
   */
  @attr("string") declare risk: RiskStringEnum;

  /**
   * Valeur = «ALD» si présente
   */
  @attr("string") declare exemption: ExemptionStringEnum;

  /**
   * DMP: PrestationFacturee::DetailDePrestation
   */
  @attr() declare prestations: any[];

  @attr("prescriptor") declare prescriptor: Prescriptor;

  @attr("performer") declare performer: Performer;

  static validations(intl: IntlService) {
    return {
      reimbursementsRetrievedJournalEvent: validatePresence({
        presence: true,
        description: intl.t(
          "validations.vaccines-refunded-journal-event.reimbursements-retrieved-journal-event"
        )
      }),
      prestations: validatePresence({
        presence: true,
        description: intl.t(
          "validations.vaccines-refunded-journal-event.prestations"
        )
      }),
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.vaccines-refunded-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      risk: validateInclusion({
        list: ["MALADIE", "MATERNITE", "ACCIDENT", "TRAVAIL", "PREVENTION"],
        allowBlank: true,
        description: intl.t("validations.vaccines-refunded-journal-event.risk")
      }),
      exemption: validateInclusion({
        list: ["ALD"],
        allowBlank: true,
        description: intl.t(
          "validations.vaccines-refunded-journal-event.exemption"
        )
      })
    };
  }

  //---- End of generated code ----
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "vaccines-refunded-journal-event": VaccinesRefundedJournalEvent;
  }
}
